export type TipoEntrega = 'INMEDIATA' | 'PROGRAMADA';
export type TipoAudiencia = 'TODOS' | 'FILTROS';
export type Sexo = 'AMBOS' | 'FEMENINO' | 'MASCULINO';
export type EstadoNotificacion = 'ACTIVA' | 'ELIMINADA';

export type TipoEnlace = 'SIN_ENLACE' | 'EXTERNO' | 'SECCION_APP';

export interface CatalogoItem {
  id: number;
  nombre: string;
}

// Catálogo de secciones de la app habilitadas como destino del enlace — lo
// administra Sistemas (GET /secciones-enlace). El id es un código ("COBERTURA"),
// no numérico como CatalogoItem, y es lo único que se guarda: nunca una ruta.
export interface SeccionEnlace {
  id: string;
  nombre: string;
}

// Enlace opcional del pop-up. En el response siempre viene (nunca null): sin
// enlace llega { tipo: 'SIN_ENLACE', destino: null, textoBoton: null }.
// destino = URL si tipo=EXTERNO, id de SeccionEnlace si tipo=SECCION_APP.
export interface EnlaceNotificacion {
  tipo: TipoEnlace;
  destino: string | null;
  textoBoton: string | null;
}

export const TEXTO_BOTON_ENLACE_SUGERIDO = 'Ver más';
export const MAX_URL_ENLACE = 2000;
export const MAX_TEXTO_BOTON_ENLACE = 25;

export const ETIQUETA_TIPO_ENLACE: Record<TipoEnlace, string> = {
  SIN_ENLACE: 'Sin enlace',
  EXTERNO: 'Enlace externo',
  SECCION_APP: 'Sección de la app'
};

export type CampoEnlace = 'url' | 'seccion' | 'textoBoton';

// El backend no devuelve el error por campo — es un único mensaje en el 400.
// Se asocia al campo por el prefijo de los mensajes de validación del enlace.
export function campoDeErrorEnlace(mensaje: string | undefined): CampoEnlace | null {
  if (!mensaje) return null;
  if (mensaje.startsWith('La URL del enlace')) return 'url';
  if (mensaje.startsWith('La sección de destino')) return 'seccion';
  if (mensaje.startsWith('El texto del botón')) return 'textoBoton';
  return null;
}

// Localidad va emparejada con su provincia a propósito — hay localidades
// homónimas en distintas provincias (ej. "Merlo" en Córdoba y en San Luis),
// localidad sola no alcanza para saber a cuál se refiere. Contrato con ids
// (corrección #9, docs/specs/abmc-notificacion-institucional.md §9.10) — el
// id es lo que permite matchear de forma confiable al editar, sin depender
// de que el nombre sea idéntico carácter a carácter contra el catálogo.
export interface LocalidadNotificacion {
  provinciaId: number;
  provincia: string;
  localidadId: number;
  localidad: string;
}

export interface NotificacionInstitucional {
  id: number;
  // Solo uso interno, nunca visible al afiliado (corrección post-implementación
  // #7 de la spec — revierte la #6, que lo había hecho el título visible).
  nombreInterno: string;
  // Título visible al afiliado (push + pop-up) — campo separado de
  // nombreInterno, agregado en la corrección #7.
  titulo: string;
  // Nombres de wire renombrados por backend en la corrección #7 (antes
  // mensajeCorto/mensajeAmpliado). Los FormControl de Angular en
  // notificacion-nueva.component.ts se quedan con los nombres en español —
  // el mapeo pasa por onSubmit()/hidratarDesde(), no acá.
  body: string;
  detalle: string;
  tipoEntrega: TipoEntrega;
  fechaProgramacion: string | null;
  fechaEnvio: string | null;
  // Corrección #10 (docs/specs/abmc-notificacion-institucional.md §9.11) —
  // snapshot histórico calculado por backend en el momento real del envío
  // (INMEDIATA o PROGRAMADA, lo que importa es que fechaEnvio ya esté
  // seteado), no una estimación en vivo. `null` hasta que se dispare — y en
  // INMEDIATA puede seguir en `null` un instante en la respuesta del mismo
  // POST que crea la notificación (el cálculo real termina unos ms después);
  // se resuelve solo en el siguiente GET.
  cantidadDestinatarios: number | null;
  tipoAudiencia: TipoAudiencia;
  sexo: Sexo | null;
  // {id, nombre} — corrección #9 (docs/specs/abmc-notificacion-institucional.md
  // §9.10): antes viajaba solo el nombre, sin id, lo que rompía el matcheo al
  // editar si el string no coincidía exacto contra el catálogo. [] = sin
  // restricción en esa categoría. No hay flag `todos` en el wire — eso es
  // puramente frontend (§9.9).
  provincias: CatalogoItem[];
  localidades: LocalidadNotificacion[];
  // {id, nombre} — corrección #9: Cobertura sí tiene id propio en la tabla
  // real (`id_objeto`), antes no se exponía (corrección #8 original).
  coberturas: CatalogoItem[];
  // Reemplaza a rangoEtarioIds (corrección #7): rango numérico libre, no
  // catálogo. null en cualquiera de los dos = sin restricción en ese extremo.
  edadDesde: number | null;
  edadHasta: number | null;
  // Las notificaciones anteriores al enlace también vienen como SIN_ENLACE.
  enlace: EnlaceNotificacion;
  estado: EstadoNotificacion;
  fechaBaja: string | null;
  usuarioAlta: string;
  usuarioModificacion: string | null;
  usuarioBaja: string | null;
  fechaCreacion: string;
  fechaModificacion: string | null;
  // Calculado por backend = (estado=ACTIVA && tipoEntrega=PROGRAMADA && fechaEnvio==null).
  // El frontend nunca recalcula esta regla, solo la lee.
  editable: boolean;
}

export interface NotificacionesFiltros {
  pagina: number;
  tamanoPagina: number;
  tipoEntrega?: TipoEntrega;
  nombreInterno?: string;
  incluirEliminadas: boolean;
}

export interface NotificacionesPaginadas {
  contenido: NotificacionInstitucional[];
  paginaActual: number;
  tamanoPagina: number;
  hayMas: boolean;
}

export interface CrearNotificacionInstitucionalRequest {
  nombreInterno: string;
  titulo: string;
  body: string;
  detalle: string;
  tipoEntrega: TipoEntrega;
  fechaProgramacion: string | null;
  tipoAudiencia: TipoAudiencia;
  sexo: Sexo | null;
  // [] o ausente = sin restricción (confirmado por backend, ver §9.9). No se
  // manda ningún flag `todos` — no existe en el contrato real; la distinción
  // "Todos" vs. "nada elegido todavía" es pura UX del frontend (signals
  // xTodos/xIds en los componentes), nunca serializada acá.
  provincias: CatalogoItem[];
  localidades: LocalidadNotificacion[];
  coberturas: CatalogoItem[];
  // Reemplaza a rangoEtarioIds (corrección #7). null = sin restricción en ese
  // extremo; si los dos vienen, edadDesde <= edadHasta (backend responde 400
  // si no) — el frontend valida esto antes de habilitar el submit.
  edadDesde: number | null;
  edadHasta: number | null;
  enlace: EnlaceNotificacion;
}

// Mismos campos y misma obligatoriedad que Crear (spec §7).
export type EditarNotificacionInstitucionalRequest = CrearNotificacionInstitucionalRequest;

const ARGENTINA_TIME_ZONE = 'America/Argentina/Buenos_Aires';

function partesFechaHoraArgentina(fecha: Date): { year: string; month: string; day: string; hour: string; minute: string } {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ARGENTINA_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(fecha);
  const valor = (tipo: string) => partes.find(p => p.type === tipo)?.value ?? '00';
  return { year: valor('year'), month: valor('month'), day: valor('day'), hour: valor('hour'), minute: valor('minute') };
}

/** "YYYY-MM-DDTHH:mm" — valor crudo de un <input type="datetime-local">. */
export function fechaHoraArgentinaActual(): string {
  const p = partesFechaHoraArgentina(new Date());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Inverso de datetimeLocalAIsoArgentina: de un ISO (cualquier offset) a valor de datetime-local en horario Argentina. */
export function isoADatetimeLocalArgentina(iso: string): string {
  const p = partesFechaHoraArgentina(new Date(iso));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// Sin offset a propósito: el backend espera LocalDateTime (sin zona), siempre
// asumido horario Argentina — mandarle un ISO con "-03:00" hace que Jackson
// rechace el body entero (no matchea contra LocalDateTime).
/** Convierte el valor de un datetime-local (asumido horario Argentina) al formato que espera el backend (sin offset). */
export function datetimeLocalAIsoArgentina(valorDatetimeLocal: string): string {
  return `${valorDatetimeLocal}:00`;
}

/** true si el datetime-local ingresado es posterior al momento actual en Argentina. */
export function esFechaFuturaEnArgentina(valorDatetimeLocal: string): boolean {
  return datetimeLocalAIsoArgentina(valorDatetimeLocal) > datetimeLocalAIsoArgentina(fechaHoraArgentinaActual());
}

/** Formatea un ISO (de cualquier offset) para mostrar en horario de Argentina, dd/mm/aaaa hh:mm. */
export function formatearFechaHoraArgentina(iso: string): string {
  return new Intl.DateTimeFormat('es-AR', {
    timeZone: ARGENTINA_TIME_ZONE,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).format(new Date(iso));
}
